import React, { FC, useState, useEffect } from 'react';
import {
  Theme,
  createStyles,
  makeStyles,
  Typography,
  Box,
  Divider,
  useTheme,
  Button,
} from '@material-ui/core';
import { useTranslation } from '../../../../../hooks/useTranslation';
import {
  AccountDetail,
  PremiseInfo,
  Maybe,
} from '../../../../../__generated__/pge-types';
import useUtils, { AccountDetailsList } from '../../utils';
import AccountSearch from '../../../../account-search/AccountSearch';
import colors from '../../../../../themes/main-colors';
import BottomDrawerLayout, {
  DrawerSnapType,
} from '../../bottom-drawer-layout/BottomDrawerLayout';

type Props = {
  accounts: AccountDetail[];
  onClose: () => void;
  handleSelectAccountAndPremise: (
    accountDetail: AccountDetail,
    premise: PremiseInfo,
    hasKnownOutage: boolean,
    hasMultiplePremises: boolean,
  ) => void;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modalWrapper: {
      width: '100%',
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column',
      paddingBottom: '36px',
    },
    headerContent: {
      letterSpacing: '1px',
      marginBottom: '16px',
    },
    content: {
      marginTop: '1rem',
      backgroundColor: colors.modalBackgroundColor,
      padding: theme.spacing(2),
      borderRadius: '6px',
    },
    accountCard: {
      padding: theme.spacing(3, 3),
      background: 'white',
      border: 'none',
      borderRadius: 6,
    },
    cardSearch: {
      marginTop: '3px',
    },
    backButton: {
      textDecoration: 'underline',
      color: colors.sparkBlue,
      width: '100%',
      marginTop: '1rem',
    },
  }),
);

const SelectAccount: FC<Props> = ({
  accounts,
  onClose,
  handleSelectAccountAndPremise,
}) => {
  const { getOutageAccountDetails } = useUtils();
  const theme = useTheme();
  const classes = useStyles();
  const { t, richT } = useTranslation();

  const [accountDetailsList, setAccountDetailsList] = useState<
    AccountDetailsList[]
  >();
  const [searchAccountQuery, setSearchAccountQuery] = useState<string>('');

  useEffect(() => {
    if (accounts) {
      setAccountDetailsList(getOutageAccountDetails(accounts));
    }
  }, []);

  const filterAccountsBySearchQuery = (accountsList: AccountDetailsList[]) => {
    return accountsList.filter(account =>
      searchAccountQuery.length === 0
        ? true
        : Object.values(account).find(item =>
            typeof item === 'string'
              ? item.toLowerCase().includes(searchAccountQuery.toLowerCase())
              : item && item.toString
              ? item
                  .toString()
                  .toLowerCase()
                  .includes(searchAccountQuery.toLowerCase())
              : false,
          ),
    );
  };

  return (
    <BottomDrawerLayout
      data-testid={'select-account-options'}
      onClose={onClose}
      defaultSnap={DrawerSnapType.default}
    >
      <Box className={classes.modalWrapper}>
        <Box className={classes.headerContent}>
          <Typography
            variant={'h3'}
            css={{
              marginBottom: '8px',
              '&>p': {
                marginBlockEnd: 0,
                marginBlockStart: 0,
                fontSize: '24px',
              },
            }}
          >
            {richT('SELECT_AN_ACCOUNT_TO_REPORT_AN_OUTAGE')}
          </Typography>
          <Typography
            css={{
              fontSize: '1rem',
              '&>p': {
                marginBlockEnd: 0,
                marginBlockStart: 0,
                fontFamily: 'Forma-DJR-Display',
              },
            }}
          >
            {richT('SELECT_AN_ACCOUNT_PREMISE_TO_REPORT_AN_OUTAGE')}
          </Typography>
        </Box>
        <Box
          css={{ marginTop: theme.spacing(3), width: '88%', margin: 'auto' }}
        >
          <AccountSearch
            onSearch={(search: string) => {
              setSearchAccountQuery(search);
            }}
            value={searchAccountQuery}
            styleClass={classes.cardSearch}
            hideSearchIcon={true}
            maxLength={10}
          />
        </Box>
        <Box className={classes.content}>
          <Box>
            {accountDetailsList &&
              filterAccountsBySearchQuery(accountDetailsList).map(
                accountDetail => {
                  const tag = accountDetail.hasKnownOutage
                    ? ` (${t('OUTAGE_REPORTED')})`
                    : accountDetail.isPrimaryAccount
                    ? ` (${t('PRIMARY')})`
                    : '';
                  const premise =
                    accountDetail?.premiseInfos &&
                    accountDetail?.premiseInfos[0];

                  const nickName: Maybe<string> | undefined = accounts.find(
                    account =>
                      accountDetail.accountNumber === account.accountNumber,
                  )?.description;
                  return (
                    <Box
                      css={{
                        '&:last-child>hr': {
                          display: 'none',
                        },
                      }}
                    >
                      <Box
                        css={{
                          cursor: 'pointer',
                          paddingLeft: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                        onClick={() => {
                          const selectAccountDetail:
                            | AccountDetail
                            | undefined = accounts.find(
                            item =>
                              item.accountNumber ===
                              accountDetail.accountNumber,
                          );
                          const hasMultiplePremises = Boolean(
                            accountDetail?.premiseInfos &&
                              accountDetail?.premiseInfos.length > 1,
                          );
                          if (selectAccountDetail && premise) {
                            handleSelectAccountAndPremise(
                              selectAccountDetail,
                              premise,
                              accountDetail.hasKnownOutage,
                              hasMultiplePremises,
                            );
                          }
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle2"
                            css={{ color: colors.sparkBlue, fontSize: '1rem' }}
                          >
                            {accountDetail.accountNumber}
                          </Typography>
                          <Typography
                            variant="body2"
                            css={{ fontSize: '0.875rem', lineHeight: 1.43 }}
                          >
                            {premise?.addressLine1}
                          </Typography>
                        </Box>
                        <Box css={{ margin: '0 16px', width: 0, flexGrow: 1 }}>
                          {nickName && (
                            <Typography
                              css={{
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                textAlign: 'right',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {nickName}
                            </Typography>
                          )}
                          <Typography
                            css={{
                              color: colors.errorRed,
                              fontSize: '0.8rem',
                              fontWeight: 700,
                              textAlign: 'right',
                            }}
                          >
                            {tag}
                          </Typography>
                        </Box>
                      </Box>
                      <Divider
                        css={{
                          margin: '16px 0',
                          backgroundColor: colors.white,
                          height: '2px',
                        }}
                      />
                    </Box>
                  );
                },
              )}
          </Box>
        </Box>
        <Button onClick={() => onClose()} className={classes.backButton}>
          <Typography>{t('BACK')}</Typography>
        </Button>
      </Box>
    </BottomDrawerLayout>
  );
};

export default SelectAccount;
